import axios from 'axios';
import { axiosInstance as apiClient } from './axiosInstance';
import { getGraphBuilderBaseUrl } from '../utils/env';
import { getEmbedAuth } from '../utils/embedAuth';
import { createSynapseService } from './synapse';
import { createNodeService } from './node';
import { createNodeKindService } from './nodekind';

import { createSynapseKindService } from './synapsekind';

// ============================================================================
// GraphBuilder API Client (separate backend on port 8000)
// ============================================================================
const GRAPH_BUILDER_BASE_URL = getGraphBuilderBaseUrl();

const graphBuilderClient = axios.create({
  baseURL: GRAPH_BUILDER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
});

// Add request interceptor for logging + tenant attribution.
// Forwards `X-CM-Account-Id` so GraphBuilder's MeteringMiddleware can
// attribute usage events to the correct CompanyAccount.
graphBuilderClient.interceptors.request.use(
  (config) => {
    try {
      const userRaw = localStorage.getItem('userData') || localStorage.getItem('user');
      if (userRaw) {
        const u = JSON.parse(userRaw);
        const accountId = u?.account_id || u?.AccountId || u?.accountId;
        if (accountId && config.headers && !config.headers['X-CM-Account-Id']) {
          config.headers['X-CM-Account-Id'] = accountId;
        }
      }
    } catch (e) { /* non-fatal */ }
    // Embed mode forwarding: when an embed token is active, surface the
    // partner-account id to GraphBuilder so its metering middleware
    // attributes usage to the right CompanyAccount. GraphBuilder itself
    // does not validate the token (it sits behind nginx) but we forward
    // it for completeness and so the Flask backend's session middleware
    // sees consistent headers if it ever proxies through.
    const embed = getEmbedAuth();
    if (embed && embed.token && config.headers) {
      config.headers['X-CM-Embed-Token'] = embed.token;
      if (embed.accountId && !config.headers['X-CM-Account-Id']) {
        config.headers['X-CM-Account-Id'] = embed.accountId;
      }
    }
    console.log(`[GraphBuilder] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
graphBuilderClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('[GraphBuilder] API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * GraphBuilder API Service
 * Connects to the GraphBuilder backend (FastAPI) on port 8000
 */
export const graphBuilderApi = {
  /**
   * Run the NL-to-Platform pipeline
   * @param {Object} payload - Pipeline request payload
   * @param {string} [payload.nl_query] - Natural language query
   * @param {Object} [payload.bounded_json] - Single JSON payload for similarity matching
   * @param {Array} [payload.bounded_json_array] - Array of JSON payloads for multi-entity triggering
   * @param {string} [payload.execution_mode] - BUILD_AND_RUN or BUILD_ONLY
   * @param {string} [payload.workspace_id] - Workspace ID override
   * @param {string} [payload.graph_id] - Graph ID override
   * @param {number} [payload.min_similarity] - Minimum similarity threshold for JSON mode
   * @param {string} [payload.snn_execution_mode] - compiled_sharded, compiled_full, or not_compiled
   * @param {number} [payload.similarity_top_k] - Number of candidate nodes from semantic search
   * @param {boolean} [payload.decompose_query] - Whether to split compound queries
   * @param {string} [payload.nl_prompt] - Additional NL prompt for LLM context
   * @returns {Promise<Object>} Pipeline response with natural_language_answer, score, top_outcome_nodes, metadata
   */
  runPipeline: async (payload) => {
    const response = await graphBuilderClient.post('/api/pipeline/run', payload);
    return response.data;
  },

  /**
   * Lightweight route-only query against a Router graph (~200ms)
   * @param {string} routerGraphId - UUID of the Router graph
   * @param {string} nlQuery - Natural language query to route
   * @returns {Promise<Object>} { target_graph_id, target_graph_name, confidence, ambiguous, all_graph_scores }
   */
  routeQuery: async (routerGraphId, nlQuery) => {
    const response = await graphBuilderClient.post('/api/pipeline/route', {
      nl_query: nlQuery,
      router_graph_id: routerGraphId,
    });
    return response.data;
  },

  /**
   * Run the NL-to-Platform pipeline with streaming SSE response
   * @param {Object} payload - Pipeline request payload (same as runPipeline)
   * @param {Function} onProgress - Callback for progress events: ({phase, message}) => void
   * @param {Function} onToken - Callback for token events: (tokenContent) => void
   * @param {Function} onRefinedAnswer - Callback when full answer is replaced (e.g. auto-requery): (newContent) => void
   * @param {Function} onComplete - Callback for completion: (metadata) => void
   * @param {Function} onError - Callback for errors: (error) => void
   * @returns {Promise<void>}
   */
  runPipelineStream: async (payload, { onProgress, onToken, onTokenReplace, onRefinedAnswer, onComplete, onError, onGraphStart, onGraphComplete, onGraphProgress, onGraphToken, onGraphTokenReplace }) => {
    const url = `${GRAPH_BUILDER_BASE_URL}/api/pipeline/run`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ ...payload, stream: true }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // FastAPI returns ``detail`` as a string for HTTPException but as a
        // structured array/object for validation errors and our metering
        // 402 responses (``{success, error_code, message, detail: {...}}``).
        // ``new Error(<object>)`` stringifies to "[object Object]", so we
        // render the most useful textual field we can find instead.
        let msg = '';
        if (typeof errorData.message === 'string') {
          msg = errorData.message;
        } else if (typeof errorData.detail === 'string') {
          msg = errorData.detail;
        } else if (errorData.detail) {
          try { msg = JSON.stringify(errorData.detail); } catch (_) { msg = String(errorData.detail); }
        } else if (errorData.error_code) {
          msg = String(errorData.error_code);
        }
        if (!msg) {
          msg = `HTTP ${response.status} ${response.statusText || ''}`.trim();
        }
        const err = new Error(msg);
        err.status = response.status;
        err.error_code = errorData.error_code;
        err.detail = errorData.detail;
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'progress':
                  onProgress?.(data);
                  break;
                case 'token':
                  onToken?.(data.content);
                  break;
                case 'token_replace':
                  onTokenReplace?.(data.content);
                  break;
                case 'refined_answer':
                  onRefinedAnswer?.(data.content);
                  break;
                case 'complete':
                  onComplete?.(data);
                  break;
                case 'graph_start':
                  onGraphStart?.(data);
                  break;
                case 'graph_progress':
                  onGraphProgress?.(data);
                  break;
                case 'graph_token':
                  onGraphToken?.(data);
                  break;
                case 'graph_token_replace':
                  onGraphTokenReplace?.(data);
                  break;
                case 'graph_complete':
                  onGraphComplete?.(data);
                  break;
                case 'error':
                  onError?.(new Error(data.message));
                  break;
                case 'metadata':
                  break;
                default:
                  console.log('[Stream] Unknown event type:', data.type);
              }
            } catch (parseErr) {
              console.warn('[Stream] Failed to parse SSE data:', line);
            }
          }
        }
      }
    } catch (err) {
      console.error('[GraphBuilder] Streaming error:', err);
      onError?.(err);
      throw err;
    }
  },

  /**
   * Compile a graph to optimized NumPy artifacts (full mode)
   * @param {string} graphId - The graph UUID to compile
   * @param {string} [workspaceId] - Optional workspace context
   * @returns {Promise<Object>} Compile response with stats and artifact paths
   */
  compileGraph: async (graphId, workspaceId = null, providerId = null, enableSer = false) => {
    const payload = {
      graph_id: graphId,
      ...(workspaceId && { workspace_id: workspaceId }),
      ...(providerId && { provider_id: providerId }),
      ...(enableSer && { enable_ser: true }),
    };
    const response = await graphBuilderClient.post('/api/loader/compile', payload);
    return response.data;
  },

  /**
   * Compile a graph to sharded artifacts (partial/node-level mode)
   * @param {string} graphId - The graph UUID to compile
   * @param {string} [workspaceId] - Optional workspace context
   * @param {Object} [providerConfig] - Optional LLM provider config for embedding
   * @returns {Promise<Object>} Compile response with stats and artifact paths
   */
  compileGraphSharded: async (graphId, workspaceId = null, providerId = null) => {
    const payload = {
      graph_id: graphId,
      ...(workspaceId && { workspace_id: workspaceId }),
      ...(providerId && { provider_id: providerId }),
    };
    const response = await graphBuilderClient.post('/api/loader/compile/sharded', payload);
    return response.data;
  },

  /**
   * Check whether compiled artifacts exist for a graph
   * @param {string} graphId - The graph UUID to check
   * @returns {Promise<Object>} { graph_id, compiled_full: bool, compiled_sharded: bool }
   */
  getCompileStatus: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/loader/compile/status/${graphId}`);
    return response.data;
  },

  /**
   * Submit an async compile job. Returns immediately with a job_id.
   * The caller should poll getCompileJob(jobId) for progress.
   *
   * Throws an axios error with status 409 when a compile is already running
   * for this graph; in that case error.response.data.detail contains
   * { existing_job_id, status, phase, percent } so the caller can re-attach.
   *
   * @param {string} graphId
   * @param {Object} opts
   * @param {('full'|'sharded')} [opts.mode='full']
   * @param {string|null} [opts.workspaceId]
   * @param {string|null} [opts.providerId]
   * @param {boolean} [opts.enableSer=false]
   * @returns {Promise<{job_id:string, graph_id:string, status:string, message:string}>}
   */
  compileGraphSubmit: async (graphId, { mode = 'full', workspaceId = null, providerId = null, enableSer = false } = {}) => {
    const url = mode === 'sharded'
      ? '/api/loader/compile/sharded/submit'
      : '/api/loader/compile/submit';
    const payload = {
      graph_id: graphId,
      ...(workspaceId && { workspace_id: workspaceId }),
      ...(providerId && { provider_id: providerId }),
      ...(enableSer && { enable_ser: true }),
    };
    const response = await graphBuilderClient.post(url, payload, { timeout: 30000 });
    return response.data;
  },

  /**
   * Poll the live status of a queued/running/completed compile job.
   * @param {string} jobId
   */
  getCompileJob: async (jobId) => {
    const response = await graphBuilderClient.get(
      `/api/loader/compile/job/${jobId}`,
      { timeout: 10000 }
    );
    return response.data;
  },

  /**
   * Look up whether there is an active compile job for the given graph.
   * Used by the UI to recover state after a page refresh.
   * @param {string} graphId
   * @returns {Promise<{graph_id:string, active:boolean, job_id?:string, status?:string, mode?:string}>}
   */
  getActiveCompileJob: async (graphId) => {
    const response = await graphBuilderClient.get(
      `/api/loader/compile/active/${graphId}`,
      { timeout: 10000 }
    );
    return response.data;
  },

  /**
   * Pre-load a graph for fast query execution
   * @param {string} graphId - The graph UUID to pre-load
   * @returns {Promise<Object>} Preload result with load_time_ms, neurons, synapses
   */
  preloadGraph: async (graphId) => {
    const response = await graphBuilderClient.post('/api/runtime/load-graph', { graph_id: graphId });
    return response.data;
  },

  /**
   * Get status of all pre-loaded graphs
   * @returns {Promise<Object>} { loaded_graphs: [...], count: number }
   */
  getPreloadStatus: async () => {
    const response = await graphBuilderClient.get('/api/runtime/loaded-graphs');
    return response.data;
  },

  /**
   * Unload a pre-loaded graph from memory
   * @param {string} graphId - The graph UUID to unload
   * @returns {Promise<Object>} { graph_id, was_loaded, message }
   */
  unloadGraph: async (graphId) => {
    const response = await graphBuilderClient.delete(`/api/runtime/load-graph/${graphId}`);
    return response.data;
  },

  // Unified graph caching (compile + pool + DB cache in one call) -- legacy
  // synchronous endpoint. Blocks until every layer finishes; subject to the
  // 120s axios timeout when the worker has to actually compile a large graph.
  // Prefer cacheGraphSubmit() + getCacheJob() for the polling pattern.
  cacheGraph: async (graphId, workspaceId = null, providerId = null) => {
    const response = await graphBuilderClient.post('/api/cache/graph', {
      graph_id: graphId,
      ...(workspaceId && { workspace_id: workspaceId }),
      ...(providerId && { provider_id: providerId }),
    });
    return response.data;
  },

  /**
   * Submit an async cache job. Returns immediately with a job_id.
   * The caller should poll getCacheJob(jobId) for progress.
   *
   * Throws an axios error with status 409 when a cache job is already
   * running for this graph; in that case error.response.data.detail
   * contains { existing_job_id, status, phase, percent } so the caller
   * can re-attach to the existing job instead of resubmitting.
   *
   * @param {string} graphId
   * @param {Object} opts
   * @param {string|null} [opts.workspaceId]
   * @param {string|null} [opts.providerId]
   * @param {boolean} [opts.fanoutWorkers=false]
   * @param {boolean} [opts.pushToRedis=false] -- default false to avoid OOM
   *   on large graphs where pushing the multi-GB synapse_embeddings.npz
   *   into Redis blows past the host memory budget. Loaders fall back
   *   to disk via the process LRU; Redis push is purely a (small) cold
   *   start optimisation.
   * @returns {Promise<{job_id:string, graph_id:string, status:string, message:string}>}
   */
  cacheGraphSubmit: async (graphId, { workspaceId = null, providerId = null, fanoutWorkers = false, pushToRedis = false } = {}) => {
    const payload = {
      graph_id: graphId,
      ...(workspaceId && { workspace_id: workspaceId }),
      ...(providerId && { provider_id: providerId }),
      fanout_workers: fanoutWorkers,
      push_to_redis: pushToRedis,
    };
    const response = await graphBuilderClient.post('/api/cache/graph/submit', payload, { timeout: 30000 });
    return response.data;
  },

  /**
   * Poll the live status of a queued/running/completed cache job.
   * @param {string} jobId
   */
  getCacheJob: async (jobId) => {
    const response = await graphBuilderClient.get(
      `/api/cache/job/${jobId}`,
      { timeout: 10000 }
    );
    return response.data;
  },

  /**
   * Look up whether there is an active cache job for the given graph.
   * Used by the UI to recover state after a page refresh.
   * @param {string} graphId
   */
  getActiveCacheJob: async (graphId) => {
    const response = await graphBuilderClient.get(
      `/api/cache/active/${graphId}`,
      { timeout: 10000 }
    );
    return response.data;
  },

  deleteCacheGraph: async (graphId, layers = null) => {
    const params = layers ? { layers: layers.join(',') } : {};
    const response = await graphBuilderClient.delete(`/api/cache/graph/${graphId}`, { params });
    return response.data;
  },

  getCacheStatus: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/cache/status/${graphId}`);
    return response.data;
  },

  /**
   * Execute a process workflow
   * @param {Object} payload - Process execution request
   * @param {string} payload.process_id - Process definition ID to execute
   * @param {Object} [payload.inputs] - Initial inputs for the process
   * @param {string} [payload.execution_mode] - 'full' or 'step'
   * @param {string} [payload.workspace_id] - Workspace ID
   * @returns {Promise<Object>} Execution response with status, context, completed_nodes, etc.
   */
  executeProcess: async (payload) => {
    const response = await graphBuilderClient.post('/api/process/execute', payload);
    return response.data;
  },

  /**
   * Get process execution status
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Current execution state
   */
  getProcessStatus: async (executionId) => {
    const response = await graphBuilderClient.get(`/api/process/status/${executionId}`);
    return response.data;
  },

  /**
   * List process definitions
   * @param {Object} [params] - Query parameters
   * @returns {Promise<Array>} List of process definitions
   */
  listProcessDefinitions: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/process/definitions', { params });
    return response.data;
  },

  /**
   * Get process execution history
   * @param {string} executionId - Execution ID
   * @returns {Promise<Array>} Execution history events
   */
  getProcessHistory: async (executionId) => {
    const response = await graphBuilderClient.get(`/api/process/history/${executionId}`);
    return response.data;
  },

  /**
   * Get all node execution results for an execution (on-demand, not polled).
   * @param {string} executionId
   * @returns {Promise<Object>} Map of nodeId -> NodeExecutionResult
   */
  getNodeResults: async (executionId) => {
    const response = await graphBuilderClient.get(`/api/process/status/${executionId}/node-results`);
    return response.data;
  },

  /**
   * Get a single node's execution result (on-demand, when user clicks a node).
   * @param {string} executionId
   * @param {string} nodeId
   * @returns {Promise<Object>} NodeExecutionResult
   */
  getNodeResult: async (executionId, nodeId) => {
    const response = await graphBuilderClient.get(`/api/process/status/${executionId}/node-results/${nodeId}`);
    return response.data;
  },

  /**
   * Get a single process definition
   * @param {string} processId - Process definition ID
   * @returns {Promise<Object>} Process definition
   */
  getProcessDefinition: async (processId) => {
    const response = await graphBuilderClient.get(`/api/process/definition/${processId}`);
    return response.data;
  },

  /**
   * Create/define a new process
   * @param {Object} processData - Process definition data
   * @param {string} processData.name - Process name
   * @param {string} [processData.description] - Process description
   * @param {Array} processData.nodes - Process nodes
   * @param {Array} [processData.synapses] - Process synapses (connections)
   * @param {Array} [processData.gates] - Process gates (flow control)
   * @param {number} [processData.max_parallel] - Max parallel executions
   * @param {number} [processData.global_timeout_seconds] - Global timeout
   * @param {string} [processData.workspace_id] - Workspace ID
   * @param {string} [processData.graph_id] - Associated graph ID
   * @param {Array} [processData.tags] - Tags for categorization
   * @returns {Promise<Object>} Created process with process_id
   */
  createProcessDefinition: async (processData) => {
    const response = await graphBuilderClient.post('/api/process/define', processData);
    return response.data;
  },

  /**
   * Update an existing process definition
   * @param {string} processId - Process ID to update
   * @param {Object} processData - Updated process data
   * @returns {Promise<Object>} Updated process
   */
  updateProcessDefinition: async (processId, processData) => {
    const response = await graphBuilderClient.put(`/api/process/definition/${processId}`, processData);
    return response.data;
  },

  /**
   * Archive (soft delete) a process definition
   * @param {string} processId - Process ID to archive
   * @returns {Promise<Object>} Result
   */
  archiveProcessDefinition: async (processId) => {
    const response = await graphBuilderClient.delete(`/api/process/definition/${processId}`);
    return response.data;
  },

  regenerateCortexProcess: async (graphId) => {
    const response = await graphBuilderClient.post(`/api/build_v8_cortex/regenerate/${graphId}`);
    return response.data;
  },

  // ==========================================================================
  // Chat Assistant module
  // ==========================================================================

  /**
   * List chat assistant summaries.
   * @param {Object} [params] - { workspace_id, status, limit, offset }
   * @returns {Promise<Object>} { success, assistants: [...], count }
   */
  listChatAssistants: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/chatassist/list', { params });
    return response.data;
  },

  /**
   * Get a full chat assistant record (including config) for editing.
   * @param {string} uuid
   */
  getChatAssistant: async (uuid) => {
    const response = await graphBuilderClient.get(`/api/chatassist/${uuid}`);
    return response.data;
  },

  /**
   * Create a chat assistant from a Test Graph snapshot.
   * @param {Object} data - { name, description, graph_id, workspace_id, account_id, config }
   */
  createChatAssistant: async (data) => {
    const response = await graphBuilderClient.post('/api/chatassist/define', data);
    return response.data;
  },

  /**
   * Update a chat assistant's metadata / presentation / status / config.
   * @param {string} uuid
   * @param {Object} data - { name?, description?, status?, config? }
   */
  updateChatAssistant: async (uuid, data) => {
    const response = await graphBuilderClient.put(`/api/chatassist/${uuid}`, data);
    return response.data;
  },

  /**
   * Soft-delete (archive) a chat assistant.
   * @param {string} uuid
   */
  archiveChatAssistant: async (uuid) => {
    const response = await graphBuilderClient.delete(`/api/chatassist/${uuid}`);
    return response.data;
  },

  /**
   * PUBLIC: fetch presentation-only config for the embeddable chat page.
   * @param {string} uuid
   * @returns {Promise<Object>} { name, title, welcome_message, suggested_questions, stream }
   */
  getChatAssistantPublic: async (uuid) => {
    const response = await graphBuilderClient.get(`/api/chatassist/public/${uuid}`);
    return response.data;
  },

  /**
   * PUBLIC: run an assistant turn through the server-side proxy.
   * @param {string} uuid
   * @param {Object} body - { nl_query, conversation_history?, last_snn_context?, session_id?, overrides? }
   */
  runChatAssistant: async (uuid, body) => {
    const response = await graphBuilderClient.post(`/api/chatassist/${uuid}/run`, body);
    return response.data;
  },

  /**
   * PUBLIC: streaming (SSE) run of an assistant turn through the proxy.
   * Mirrors runPipelineStream's SSE parsing.
   * @param {string} uuid
   * @param {Object} body - { nl_query, conversation_history?, last_snn_context?, session_id?, overrides? }
   * @param {Object} callbacks - { onProgress, onToken, onTokenReplace, onRefinedAnswer, onComplete, onError }
   */
  runChatAssistantStream: async (uuid, body, { onProgress, onToken, onTokenReplace, onRefinedAnswer, onComplete, onError, onGraphStart, onGraphComplete, onGraphProgress, onGraphToken, onGraphTokenReplace }) => {
    const url = `${GRAPH_BUILDER_BASE_URL}/api/chatassist/${uuid}/run/stream`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(body || {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let msg = '';
        if (typeof errorData.message === 'string') {
          msg = errorData.message;
        } else if (typeof errorData.detail === 'string') {
          msg = errorData.detail;
        } else if (errorData.detail) {
          try { msg = JSON.stringify(errorData.detail); } catch (_) { msg = String(errorData.detail); }
        } else if (errorData.error_code) {
          msg = String(errorData.error_code);
        }
        if (!msg) {
          msg = `HTTP ${response.status} ${response.statusText || ''}`.trim();
        }
        const err = new Error(msg);
        err.status = response.status;
        err.error_code = errorData.error_code;
        err.detail = errorData.detail;
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (data.type) {
                case 'progress':
                  onProgress?.(data);
                  break;
                case 'token':
                  onToken?.(data.content);
                  break;
                case 'token_replace':
                  onTokenReplace?.(data.content);
                  break;
                case 'refined_answer':
                  onRefinedAnswer?.(data.content);
                  break;
                case 'complete':
                  onComplete?.(data);
                  break;
                case 'graph_start':
                  onGraphStart?.(data);
                  break;
                case 'graph_progress':
                  onGraphProgress?.(data);
                  break;
                case 'graph_token':
                  onGraphToken?.(data);
                  break;
                case 'graph_token_replace':
                  onGraphTokenReplace?.(data);
                  break;
                case 'graph_complete':
                  onGraphComplete?.(data);
                  break;
                case 'error':
                  onError?.(new Error(data.message));
                  break;
                case 'metadata':
                  break;
                default:
                  console.log('[ChatAssistStream] Unknown event type:', data.type);
              }
            } catch (parseErr) {
              console.warn('[ChatAssistStream] Failed to parse SSE data:', line);
            }
          }
        }
      }
    } catch (err) {
      console.error('[GraphBuilder] Chat assistant streaming error:', err);
      onError?.(err);
      throw err;
    }
  },

  /**
   * Duplicate a process definition, optionally targeting a different graph.
   * @param {string} sourceProcessId - ID of the process to copy
   * @param {string} [targetGraphId] - Optional target graph ID. Omit for a global copy.
   * @param {string} [newName] - Optional new name for the copied process
   * @param {string} [targetWorkspaceId] - Optional target workspace ID
   * @returns {Promise<Object>} Created process with process_id
   */
  copyProcessToGraph: async (sourceProcessId, targetGraphId = null, newName = null, targetWorkspaceId = null) => {
    const body = {
      source_process_id: sourceProcessId,
      new_name: newName,
      target_workspace_id: targetWorkspaceId,
    };
    if (targetGraphId) body.target_graph_id = targetGraphId;
    const response = await graphBuilderClient.post('/api/process/copy', body);
    return response.data;
  },

  duplicateProcess: async (sourceProcessId, newName = null) => {
    const body = { source_process_id: sourceProcessId };
    if (newName) body.new_name = newName;
    const response = await graphBuilderClient.post('/api/process/copy', body);
    return response.data;
  },

  generateProcessFromNL: async (prompt, history = [], availableGraphs = [], model = 'gpt-4o-mini') => {
    const response = await graphBuilderClient.post('/api/process/generate', {
      prompt,
      history,
      available_graphs: availableGraphs,
      model,
    }, { timeout: 150000 });
    return response.data;
  },

  /**
   * List process definitions with filtering
   * @param {Object} [params] - Query parameters
   * @param {string} [params.graph_id] - Filter by graph ID
   * @param {string} [params.workspace_id] - Filter by workspace ID
   * @param {string} [params.status] - Filter by status (active, archived, draft)
   * @param {string} [params.search] - Search by name
   * @param {number} [params.page] - Page number
   * @param {number} [params.page_size] - Page size
   * @returns {Promise<Object>} List of processes with pagination
   */
  listProcessDefinitions: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/process/definitions', { params });
    return response.data;
  },

  /**
   * List executions for a process or workspace
   * @param {Object} [params] - Query parameters
   * @param {string} [params.process_id] - Filter by process ID
   * @param {string} [params.workspace_id] - Filter by workspace ID
   * @param {string} [params.status] - Filter by status
   * @returns {Promise<Object>} List of executions
   */
  listProcessExecutions: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/process/executions', { params });
    return response.data;
  },

  /**
   * Step through a process execution (for step mode)
   * @param {string} executionId - Execution ID
   * @param {string} [forceNode] - Force execution of specific node
   * @returns {Promise<Object>} Updated execution state
   */
  stepProcessExecution: async (executionId, forceNode = null) => {
    const response = await graphBuilderClient.post(`/api/process/step/${executionId}`, {
      execution_id: executionId,
      force_node: forceNode
    });
    return response.data;
  },

  /**
   * Pause a running process execution
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Updated execution state
   */
  pauseProcessExecution: async (executionId) => {
    const response = await graphBuilderClient.post(`/api/process/pause/${executionId}`);
    return response.data;
  },

  /**
   * Resume a paused process execution
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Updated execution state
   */
  resumeProcessExecution: async (executionId) => {
    const response = await graphBuilderClient.post(`/api/process/resume/${executionId}`);
    return response.data;
  },

  /**
   * Cancel a process execution
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Updated execution state
   */
  cancelProcessExecution: async (executionId) => {
    const response = await graphBuilderClient.post(`/api/process/cancel/${executionId}`);
    return response.data;
  },

  /**
   * Check if a PDF report is available for a given execution.
   * @param {string} executionId
   * @returns {Promise<Object>} { available, generating, filename, size_bytes, generated_at }
   */
  getReportStatus: async (executionId) => {
    const response = await graphBuilderClient.get(`/api/process/report/${executionId}/status`);
    return response.data;
  },

  /**
   * Get the direct download URL for a PDF report.
   * @param {string} executionId
   * @returns {string} Full URL for the report download endpoint
   */
  getReportDownloadUrl: (executionId) => {
    return `${graphBuilderClient.defaults.baseURL}/api/process/report/${executionId}`;
  },

  // A/B Testing
  executeProcessAB: async (payload) => {
    const response = await graphBuilderClient.post('/api/process/execute-ab', payload);
    return response.data;
  },
  listABRuns: async (processId) => {
    const response = await graphBuilderClient.get('/api/process/ab-runs', { params: { process_id: processId } });
    return response.data;
  },

  /**
   * Health check for GraphBuilder
   * @returns {Promise<Object>} Health status
   */
  healthCheck: async () => {
    const response = await graphBuilderClient.get('/health');
    return response.data;
  },

  /**
   * Network latency diagnostic probe.
   * Measures round-trip time to Database, Pinecone, and OpenAI.
   * @returns {Promise<Object>} { database: {status,rtt_ms}, pinecone: {status,rtt_ms}, openai: {status,rtt_ms,model}, total_rtt_ms }
   */
  latencyProbe: async () => {
    const response = await graphBuilderClient.get('/api/health/latency', { timeout: 30000 });
    return response.data;
  },

  // ============================================================================
  // Graph Consolidation API
  // ============================================================================

  /**
   * Run automated graph consolidation
   * @param {string} graphId - Graph ID to consolidate
   * @param {string} workspaceId - Workspace ID
   * @param {Object} [config] - Optional configuration overrides
   * @param {string[]} [documentIds] - Optional array of document IDs to include (for incremental consolidation)
   * @returns {Promise<Object>} Consolidation response with consolidation_id
   */
  consolidateGraph: async (graphId, workspaceId, config = null, documentIds = null) => {
    const payload = {
      workspace_id: workspaceId,
      config: config,
      document_ids: documentIds,  // Optional: only consolidate nodes from these documents
    };
    const response = await graphBuilderClient.post(`/api/graphs/${graphId}/consolidate`, payload);
    return response.data;
  },

  /**
   * Get active consolidation for a graph (if any)
   * @param {string} graphId - Graph ID
   * @returns {Promise<Object>} Active consolidation or {active: false}
   */
  getActiveConsolidation: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/graphs/${graphId}/consolidation/active`);
    return response.data;
  },

  /**
   * Get consolidation job status
   * @param {string} graphId - Graph ID
   * @param {string} consolidationId - Consolidation job ID
   * @returns {Promise<Object>} Job status
   */
  getConsolidationStatus: async (graphId, consolidationId) => {
    const response = await graphBuilderClient.get(`/api/graphs/${graphId}/consolidation/status/${consolidationId}`);
    return response.data;
  },

  /**
   * Get consolidation history for a graph
   * @param {string} graphId - Graph ID
   * @param {number} [limit=10] - Number of records to return
   * @returns {Promise<Object>} History records
   */
  getConsolidationHistory: async (graphId, limit = 10) => {
    const response = await graphBuilderClient.get(`/api/graphs/${graphId}/consolidation/history`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get review queue items for a graph
   * @param {string} graphId - Graph ID
   * @returns {Promise<Object>} Review queue items
   */
  getReviewQueue: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/graphs/${graphId}/consolidation/review-queue`);
    return response.data;
  },

  /**
   * Get count of pending review items
   * @param {string} graphId - Graph ID
   * @returns {Promise<Object>} Count of pending items
   */
  getReviewQueueCount: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/graphs/${graphId}/consolidation/review-count`);
    return response.data;
  },

  /**
   * Process a review queue item (approve or reject)
   * @param {string} graphId - Graph ID
   * @param {string} reviewId - Review item ID
   * @param {string} action - 'approve' or 'reject'
   * @param {string} [notes] - Optional review notes
   * @returns {Promise<Object>} Result of the action
   */
  processReviewItem: async (graphId, reviewId, action, notes = null) => {
    const response = await graphBuilderClient.post(
      `/api/graphs/${graphId}/consolidation/review/${reviewId}`,
      { action, notes }
    );
    return response.data;
  },

  // ============================================================================
  // Document Preprocessor API
  // ============================================================================

  /**
   * Submit a document for async preprocessing (restructuring for graph building).
   * @param {File} file - .docx or .pdf file (max 1 MB)
   * @param {string} [graphId] - Optional graph ID for custom prompt lookup
   * @returns {Promise<Object>} { job_id, status, message }
   */
  preprocessUploadAsync: async (file, graphId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (graphId) {
      formData.append('graph_id', graphId);
    }
    const response = await graphBuilderClient.post('/api/preprocess/upload/async', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  /**
   * Poll status of an async preprocessing job.
   * @param {string} jobId - Job ID from preprocessUploadAsync
   * @returns {Promise<Object>} { job_id, status, validation, download_url, ... }
   */
  preprocessStatus: async (jobId) => {
    const response = await graphBuilderClient.get(`/api/preprocess/status/${jobId}`);
    return response.data;
  },

  /**
   * Get the full download URL for a preprocessed document.
   * @param {string} jobId - Job ID
   * @returns {string} Absolute URL for downloading the .docx
   */
  preprocessDownloadUrl: (jobId) => {
    return `${GRAPH_BUILDER_BASE_URL}/api/preprocess/download/${jobId}`;
  },

  /**
   * Health check for the document preprocessor module.
   * @returns {Promise<Object>} { status, openai_available, ... }
   */
  preprocessHealth: async () => {
    const response = await graphBuilderClient.get('/api/preprocess/health');
    return response.data;
  },

  // ============================================================================
  // Prompts API - Get default prompts from GraphBuilder
  // ============================================================================

  /**
   * Get all default graph building prompts from GraphBuilder service
   * @returns {Promise<Object>} Object with prompts and metadata
   */
  getDefaultPrompts: async () => {
    const response = await graphBuilderClient.get('/api/prompts/defaults');
    return response.data;
  },

  /**
   * Re-persist a graph from saved processing_output.
   * Use when build completed but persistence failed (e.g. stale DB connection).
   * @param {string} documentId - Document ID
   * @param {string} [graphId] - Optional graph UUID (auto-resolved if not provided)
   * @param {string} [workspaceId] - Optional workspace UUID (auto-resolved if not provided)
   * @returns {Promise<Object>} Persistence result with node/synapse counts
   */
  repersistGraph: async (documentId, graphId = null, workspaceId = null) => {
    const payload = {
      document_id: documentId,
      ...(graphId && { graph_id: graphId }),
      ...(workspaceId && { workspace_id: workspaceId }),
    };
    const response = await graphBuilderClient.post(
      '/api/build_consolidated_graph/repersist',
      payload,
      { timeout: 600000 }  // 10 min timeout for large graphs (embedding is slow)
    );
    return response.data;
  },

  // ── Graph Transfer ──────────────────────────────────────────
  graphTransferEnvironments: async () => {
    const response = await graphBuilderClient.get('/api/graph-transfer/environments');
    return response.data;
  },

  graphTransferEstimate: async (graphId) => {
    const response = await graphBuilderClient.post('/api/graph-transfer/estimate', { graph_id: graphId });
    return response.data;
  },

  graphTransferExport: async (graphId) => {
    const response = await graphBuilderClient.post(
      '/api/graph-transfer/export',
      { graph_id: graphId },
      { responseType: 'blob', timeout: 0 },
    );
    return response.data;
  },

  graphTransferPreview: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await graphBuilderClient.post('/api/graph-transfer/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  graphTransferImport: async (file, targetEnv, graphName, userId, workspaceId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_env', targetEnv);
    if (graphName) formData.append('graph_name', graphName);
    if (userId) formData.append('user_id', userId);
    if (workspaceId) formData.append('workspace_id', workspaceId);
    const response = await graphBuilderClient.post('/api/graph-transfer/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  graphTransferImportLocal: async (file, graphName, userId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (graphName) formData.append('graph_name', graphName);
    if (userId) formData.append('user_id', userId);
    const response = await graphBuilderClient.post('/api/graph-transfer/import-local', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  graphTransferStream: async (graphId, targetEnv, graphName, userId, workspaceId, onEvent) => {
    const response = await fetch(`${GRAPH_BUILDER_BASE_URL}/api/graph-transfer/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        graph_id: graphId,
        target_env: targetEnv,
        graph_name: graphName || undefined,
        user_id: userId || undefined,
        workspace_id: workspaceId || undefined,
      }),
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6));
            onEvent(event);
          } catch (e) { /* skip malformed */ }
        }
      }
    }
  },

  // ============================================================================
  // Graph Links API (Knowledge Graph <-> Process Graph connections)
  // ============================================================================

  listGraphLinks: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/graph-links', { params });
    return response.data;
  },

  getGraphLink: async (linkId) => {
    const response = await graphBuilderClient.get(`/api/graph-links/${linkId}`);
    return response.data;
  },

  createGraphLink: async (data) => {
    const response = await graphBuilderClient.post('/api/graph-links', data);
    return response.data;
  },

  bulkCreateGraphLinks: async (links) => {
    const response = await graphBuilderClient.post('/api/graph-links/bulk', { links });
    return response.data;
  },

  updateGraphLink: async (linkId, data) => {
    const response = await graphBuilderClient.put(`/api/graph-links/${linkId}`, data);
    return response.data;
  },

  deleteGraphLink: async (linkId) => {
    const response = await graphBuilderClient.delete(`/api/graph-links/${linkId}`);
    return response.data;
  },

  suggestGraphLinks: async (params) => {
    const response = await graphBuilderClient.post('/api/graph-links/suggest', params);
    return response.data;
  },

  validateGraphLinks: async (processId) => {
    const response = await graphBuilderClient.post('/api/graph-links/validate', { process_id: processId });
    return response.data;
  },

  getGraphLinkSummary: async (params = {}) => {
    const response = await graphBuilderClient.get('/api/graph-links/summary', { params });
    return response.data;
  },

  /**
   * On-demand gated decision tree explanation for a pipeline run.
   * @param {string} queryRunId - The query_run_id from pipeline/run metadata
   * @param {Object} [options] - Optional overrides
   * @param {string} [options.llm_model] - LLM model override
   * @param {boolean} [options.include_alternatives] - Generate what-if scenarios
   * @param {Object} [options.provider_config] - LLM provider config
   * @returns {Promise<Object>} ExplainResponse with gated_decision_tree, gates_summary, etc.
   */
  getGatedDecisionTree: async (queryRunId, options = {}) => {
    const response = await graphBuilderClient.post(`/api/pipeline/explain/${queryRunId}`, options);
    return response.data;
  },

  // ── Cache Management (GraphBuilder) ─────────────────────────────
  listCachedGraphs: async () => {
    const response = await graphBuilderClient.get('/api/cache/graphs');
    return response.data;
  },
  getGraphCacheStatus: async (graphId) => {
    const response = await graphBuilderClient.get(`/api/cache/status/${graphId}`);
    return response.data;
  },
  deleteGraphCache: async (graphId, layers) => {
    const params = layers ? `?layers=${layers}` : '';
    const response = await graphBuilderClient.delete(`/api/cache/graph/${graphId}${params}`);
    return response.data;
  },
  deleteAllGraphCaches: async () => {
    const response = await graphBuilderClient.delete('/api/cache/all');
    return response.data;
  },

  // ── Explainability & Tracing API ────────────────────────────────

  getBusinessExplanation: async (params) => {
    const response = await graphBuilderClient.post('/api/explain/business-explanation', params);
    return response.data;
  },
  getProcessExplanation: async (executionId, domain = 'aml', includeNarrative = true) => {
    const response = await graphBuilderClient.post('/api/explain/process-explanation', {
      execution_id: executionId,
      domain,
      include_narrative: includeNarrative,
    });
    return response.data;
  },
  compareEvaluations: async (params) => {
    const response = await graphBuilderClient.post('/api/explain/compare', params);
    return response.data;
  },

  /**
   * Score a rendered answer against an expected answer using local BM25
   * + IDF-weighted token-overlap F1 (no LLM call). Used by the Test Cases
   * "Run All Tests" button.
   *
   * @param {Object} payload
   * @param {string} payload.expected
   * @param {string} payload.actual
   * @param {string[]} [payload.corpus] - Optional list of expected answers
   *   from the full suite for stable IDF weighting.
   * @param {Object}   [payload.options] - { stem, remove_stopwords, min_token_len, extra_stopwords }
   * @param {number}   [payload.top_terms=50]
   * @returns {Promise<Object>} closeness, f1, precision, recall, bm25_score,
   *   bm25_normalized, missing_terms, extra_terms, token_counts
   */
  bm25Compare: async (payload) => {
    const response = await graphBuilderClient.post('/api/evaluate/bm25-compare', payload);
    return response.data;
  },

  /**
   * Score with an LLM judge. Slower but semantic. Returns the same shape
   * as bm25Compare with an extra `reasoning` string.
   *
   * @param {Object} payload
   * @param {string} payload.expected
   * @param {string} payload.actual
   * @param {string} [payload.question]
   * @param {string} [payload.model]
   */
  llmCompare: async (payload) => {
    const response = await graphBuilderClient.post('/api/evaluate/llm-compare', payload);
    return response.data;
  },
  getEvidenceProvenance: async (executionId, domain = 'general', workspaceId = null) => {
    const params = new URLSearchParams({ domain });
    if (workspaceId) params.append('workspace_id', workspaceId);
    const response = await graphBuilderClient.get(`/api/explain/provenance/${executionId}?${params}`);
    return response.data;
  },
  verifyIntegrity: async (traceId) => {
    const response = await graphBuilderClient.get(`/api/explain/integrity/${traceId}`);
    return response.data;
  },
  getContributions: async (executionId, domain = 'general', workspaceId = null, insightId = null) => {
    const params = new URLSearchParams({ domain });
    if (workspaceId) params.append('workspace_id', workspaceId);
    if (insightId) params.append('insight_id', insightId);
    const response = await graphBuilderClient.get(`/api/explain/contributions/${executionId}?${params}`);
    return response.data;
  },
  getProvenance: async (executionId, domain = 'general', workspaceId = null, insightId = null) => {
    const params = new URLSearchParams({ domain });
    if (workspaceId) params.append('workspace_id', workspaceId);
    if (insightId) params.append('insight_id', insightId);
    const response = await graphBuilderClient.get(`/api/explain/provenance/${executionId}?${params}`);
    return response.data;
  },
  getEvaluationTimeline: async (executionId, domain = 'general', workspaceId = null, insightId = null) => {
    const params = new URLSearchParams({ domain });
    if (workspaceId) params.append('workspace_id', workspaceId);
    if (insightId) params.append('insight_id', insightId);
    const response = await graphBuilderClient.get(`/api/explain/timeline/${executionId}?${params}`);
    return response.data;
  },
  getAvailableDomains: async () => {
    const response = await graphBuilderClient.get('/api/explain/vocab/domains');
    return response.data;
  },
  getDecisionStability: async ({ queryRunId, insightId = null, domain = 'general', workspaceId = null }) => {
    const response = await graphBuilderClient.post('/api/explain/stability', {
      query_run_id: queryRunId,
      insight_id: insightId,
      domain,
      workspace_id: workspaceId,
    });
    return response.data;
  },
  runWhatIf: async ({ queryRunId, insightId = null, scenarios, domain = 'general', workspaceId = null }) => {
    const response = await graphBuilderClient.post('/api/explain/what-if', {
      query_run_id: queryRunId,
      insight_id: insightId,
      scenarios,
      domain,
      workspace_id: workspaceId,
    });
    return response.data;
  },

  // ── Session Context SNN ─────────────────────────────────────────
  sessionContextProcessTurn: async (sessionId, text) => {
    const response = await graphBuilderClient.post(
      `/api/session-context/${sessionId}/turn`,
      { text }
    );
    return response.data;
  },
  sessionContextGetSnapshot: async (sessionId) => {
    const response = await graphBuilderClient.get(
      `/api/session-context/${sessionId}/snapshot`
    );
    return response.data;
  },
  sessionContextClear: async (sessionId) => {
    const response = await graphBuilderClient.delete(
      `/api/session-context/${sessionId}`
    );
    return response.data;
  },
  sessionContextHealth: async () => {
    const response = await graphBuilderClient.get('/api/session-context/health');
    return response.data;
  },

  // ==========================================================================
  // Categorical Deep-Chain Reasoner -- Pack Admin
  // ==========================================================================

  /**
   * List every category pack on disk plus the registry and decision policy.
   * @returns {Promise<{packs: Array, registry: Object|null, policy: Object|null}>}
   */
  listPacks: async () => {
    const response = await graphBuilderClient.get('/api/admin/packs');
    return response.data;
  },

  /**
   * Validate an uploaded YAML (pack | registry | policy) without persisting it.
   * @param {File|null} file - Multipart YAML file
   * @param {Object} options
   * @param {string} [options.yamlText] - Raw YAML text (alternative to file)
   * @param {('pack'|'registry'|'policy'|null)} [options.expectedKind]
   * @returns {Promise<{ok: boolean, kind: string, parsed: Object, errors: Array, warnings: Array, case_literals_found: Array<string>}>}
   */
  validatePack: async (file, { yamlText, expectedKind } = {}) => {
    const form = new FormData();
    if (file) form.append('file', file);
    if (yamlText) form.append('yaml_text', yamlText);
    if (expectedKind) form.append('expected_kind', expectedKind);
    const response = await graphBuilderClient.post(
      '/api/admin/packs/validate',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /**
   * Persist a previously-validated YAML to disk.
   * @param {string} yamlText - Verbatim YAML text
   * @param {('pack'|'registry'|'policy')} kind
   * @param {Object} [options]
   * @param {boolean} [options.overwrite=false]
   * @param {boolean} [options.registerInRegistry=true]
   * @returns {Promise<{ok: boolean, saved_path: string|null, registry_updated: boolean, validation: Object}>}
   */
  savePack: async (yamlText, kind, { overwrite = false, registerInRegistry = true } = {}) => {
    const response = await graphBuilderClient.post('/api/admin/packs', {
      yaml_text: yamlText,
      kind,
      overwrite,
      register_in_registry: registerInRegistry,
    });
    return response.data;
  },

  /**
   * Read the manifest-derived foundational graph tag map.
   * @returns {Promise<{workspace_id: string|null, by_graph_id: Object<string,string>, entries: Array}>}
   */
  listGraphTags: async () => {
    const response = await graphBuilderClient.get('/api/admin/graph-tags');
    return response.data;
  },

  /**
   * Bulk cheap probe of cache + SER status for many graphs at once.
   * Used by the Graph List page so we don't fan out N GET /api/cache/status/{id}
   * calls for a paginated table.
   *
   * @param {string[]} graphIds - up to ~200 UUIDs.
   * @returns {Promise<{statuses: Object<string, {
   *   compiled_full: boolean,
   *   compiled_at: string|null,
   *   has_ser_receptors: boolean,
   *   ser_compiled_at: string|null,
   *   in_redis_cache: boolean,
   *   redis_cached_at: string|null,
   *   redis_cached_at_approximate: boolean
   * }>}>}
   */
  getBulkCacheStatus: async (graphIds) => {
    const ids = Array.isArray(graphIds) ? graphIds.filter(Boolean) : [];
    if (ids.length === 0) return { statuses: {} };
    const response = await graphBuilderClient.post('/api/cache/status/bulk', {
      graph_ids: ids,
    });
    return response.data;
  },

  /**
   * Launch the foundational reference-corpus build job.
   * @param {Object} [options]
   * @param {Array<string>} [options.only] - Subset of manifest entry names to build
   * @param {boolean} [options.rebuildAll=false]
   * @returns {Promise<{job_id: string, status: string}>}
   */
  buildFoundationalCorpus: async ({ only = null, rebuildAll = false } = {}) => {
    const response = await graphBuilderClient.post(
      '/api/admin/foundational/build',
      { only, rebuild_all: rebuildAll },
    );
    return response.data;
  },

  /**
   * Poll a foundational corpus build job's status.
   * @param {string} jobId
   * @returns {Promise<Object>}
   */
  getFoundationalBuildStatus: async (jobId) => {
    const response = await graphBuilderClient.get(
      `/api/admin/foundational/build/${jobId}`,
    );
    return response.data;
  },

  /**
   * List recent foundational build jobs (in-process registry).
   * @returns {Promise<Array<Object>>}
   */
  listFoundationalBuildJobs: async () => {
    const response = await graphBuilderClient.get('/api/admin/foundational/build');
    return response.data;
  },
};

// API service functions
const synapseService = createSynapseService(apiClient);
const nodeService = createNodeService(apiClient);
const nodeKindService = createNodeKindService(apiClient);
const synapseKindService = createSynapseKindService(apiClient);

export const apiService = {
  // Generic GET method for direct API calls
  get: async (url) => {
    const response = await apiClient.get(url);
    return response;
  },

  // Generic POST method for direct API calls
  post: async (url, data) => {
    const response = await apiClient.post(url, data);
    return response;
  },

  // Generic PUT method for direct API calls
  put: async (url, data) => {
    const response = await apiClient.put(url, data);
    return response;
  },

  // Generic DELETE method for direct API calls
  delete: async (url) => {
    const response = await apiClient.delete(url);
    return response;
  },

  // Health check
  healthCheck: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Get sample data
  getSampleData: async () => {
    const response = await apiClient.get('/sample');
    return response.data;
  },

  // Test database connection
  testDatabase: async () => {
    const response = await apiClient.get('/db-test');
    return response.data;
  },

  // Authentication endpoints
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    // Store session ID, token, and user data
    if (response.data.success && response.data.sessionId) {
      localStorage.setItem('sessionId', response.data.sessionId);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      // Clear all stored data regardless of API response
      localStorage.removeItem('sessionId');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
    }
  },

  signup: async (userData) => {
    const response = await apiClient.post('/auth/signup', userData);
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  activate: async (token) => {
    const response = await apiClient.get(`/auth/activate?token=${token}`);
    return response.data;
  },

  checkEmail: async (email) => {
    const response = await apiClient.post('/auth/check-email', { email });
    return response.data;
  },

  // User Management endpoints
  getUsersList: async (page = 1, pageSize = 25, companyId) => {
    const params = { page, pageSize };
    if (companyId) {
      params.companyId = companyId;
    }
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  getUserDetails: async (userId) => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  createUser: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await apiClient.put(`/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  },

  getRoles: async () => {
    const response = await apiClient.get('/users/roles');
    return response.data;
  },

  getAccounts: async (companyId) => {
    const params = companyId ? { companyId } : {};
    const response = await apiClient.get('/users/accounts', { params });
    return response.data;
  },

  // Account Management endpoints
  listAccounts: async (page = 1, pageSize = 25, companyId) => {
    const params = { page, pageSize };
    if (companyId) {
      params.companyId = companyId;
    }
    const response = await apiClient.get('/accounts', { params });
    return response.data;
  },

  getAccountDetails: async (accountId) => {
    const response = await apiClient.get(`/accounts/${accountId}`);
    return response.data;
  },

  createAccount: async (accountData) => {
    const response = await apiClient.post('/accounts', accountData);
    return response.data;
  },

  updateAccount: async (accountId, accountData) => {
    const response = await apiClient.put(`/accounts/${accountId}`, accountData);
    return response.data;
  },

  deleteAccount: async (accountId) => {
    const response = await apiClient.delete(`/accounts/${accountId}`);
    return response.data;
  },

  checkAccountUsers: async (accountId) => {
    const response = await apiClient.get(`/accounts/${accountId}/users/count`);
    return response.data;
  },

  // Workspace Management endpoints
  listWorkspaces: async (page = 1, pageSize = 25) => {
    const params = { page, pageSize };
    const response = await apiClient.get('/workspaces', { params });
    return response.data;
  },

  getWorkspace: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}`);
    return response.data;
  },

  createWorkspace: async (workspaceData) => {
    const response = await apiClient.post('/workspaces', workspaceData);
    return response.data;
  },

  updateWorkspace: async (workspaceId, workspaceData) => {
    const response = await apiClient.put(`/workspaces/${workspaceId}`, workspaceData);
    return response.data;
  },

  deleteWorkspace: async (workspaceId, confirmationCode) => {
    const response = await apiClient.delete(`/workspaces/${workspaceId}`, {
      data: { confirmationCode }
    });
    return response.data;
  },

  checkWorkspaceCanDelete: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/can-delete`);
    return response.data;
  },

  listWorkspacePermissions: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/permissions`);
    return response.data;
  },

  grantWorkspacePermission: async (workspaceId, userId, permissionType) => {
    const response = await apiClient.post(`/workspaces/${workspaceId}/permissions`, {
      userId,
      permissionType
    });
    return response.data;
  },

  revokeWorkspacePermission: async (workspaceId, accessControlId) => {
    const response = await apiClient.delete(`/workspaces/${workspaceId}/permissions/${accessControlId}`);
    return response.data;
  },

  searchUsers: async (searchTerm) => {
    const params = searchTerm ? { searchTerm } : {};
    const response = await apiClient.get('/workspaces/search-users', { params });
    return response.data;
  },

  // Graph Management endpoints
  listGraphs: async (page = 1, pageSize = 25, { workspaceId, search } = {}) => {
    const params = { page, pageSize };
    if (workspaceId) params.workspaceId = workspaceId;
    if (search) params.search = search;
    const response = await apiClient.get('/graphs', { params });
    return response.data;
  },

  // Prompt Template Management
  listPromptTemplates: async (params = {}) => {
    const response = await apiClient.get('/prompt-templates', { params });
    return response.data;
  },

  getPromptTemplate: async (templateId) => {
    const response = await apiClient.get(`/prompt-templates/${templateId}`);
    return response.data;
  },

  createPromptTemplate: async (templateData) => {
    const response = await apiClient.post('/prompt-templates', templateData);
    return response.data;
  },

  updatePromptTemplate: async (templateId, templateData) => {
    const response = await apiClient.put(`/prompt-templates/${templateId}`, templateData);
    return response.data;
  },

  deletePromptTemplate: async (templateId) => {
    const response = await apiClient.delete(`/prompt-templates/${templateId}`);
    return response.data;
  },

  seedSystemPrompts: async () => {
    const response = await apiClient.post('/prompt-templates/seed-system');
    return response.data;
  },

  getGraphDetails: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}`);
    return response.data;
  },

  createGraph: async (graphData) => {
    const response = await apiClient.post('/graphs', graphData);
    return response.data;
  },

  updateGraph: async (graphId, graphData) => {
    const response = await apiClient.put(`/graphs/${graphId}`, graphData);
    return response.data;
  },

  deleteGraph: async (graphId, confirmationCode) => {
    const response = await apiClient.delete(`/graphs/${graphId}`, {
      data: { confirmationCode }
    });
    return response.data;
  },

  checkGraphCanDelete: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/can-delete`);
    return response.data;
  },

  /**
   * Reprocess a graph by clearing all graph data (nodes, synapses, Q/A)
   * while preserving chunks and chunk Q/A.
   * @param {string} graphId - The graph UUID
   * @param {string} confirmationCode - Must be "REPROCESS" to confirm
   * @returns {Promise<Object>} Result with deleted counts
   */
  reprocessGraph: async (graphId, confirmationCode) => {
    const response = await apiClient.post(`/graphs/${graphId}/reprocess`, {
      confirmationCode
    });
    return response.data;
  },

  /**
   * Resume graph processing from last checkpoint
   * @param {string} graphId - Graph UUID
   * @param {Object} options - Optional overrides (model, chunk_retries)
   * @returns {Promise<Object>} Result with job_id and resume details
   */
  resumeGraphProcessing: async (graphId, options = {}) => {
    const response = await apiClient.post(`/graphs/${graphId}/resume`, options);
    return response.data;
  },

  getGraphPermissions: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/permissions`);
    return response.data;
  },

  grantGraphPermission: async (graphId, userId, permissionType) => {
    const response = await apiClient.post(`/graphs/${graphId}/permissions`, {
      userId,
      permissionType
    });
    return response.data;
  },

  revokeGraphPermission: async (graphId, accessControlId) => {
    const response = await apiClient.delete(`/graphs/${graphId}/permissions/${accessControlId}`);
    return response.data;
  },

  getWorkspacesForDropdown: async () => {
    const response = await apiClient.get('/graphs/workspaces-dropdown');
    return response.data;
  },

  searchGraphUsers: async (searchTerm) => {
    const params = searchTerm ? { searchTerm } : {};
    const response = await apiClient.get('/graphs/search-users', { params });
    return response.data;
  },

  // ============================================================================
  // Document Upload for Graph Building
  // ============================================================================

  uploadDocuments: async (graphId, files, onProgress, processingParams = null) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('graphId', graphId);
    
    // Include processing params if provided (for chunking strategy, model, etc.)
    if (processingParams) {
      formData.append('processing_params', JSON.stringify(processingParams));
    }
    
    const response = await apiClient.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 300000 // 5 min – large file uploads can be slow
    });
    return response.data;
  },

  getGraphDocuments: async (graphId) => {
    const response = await apiClient.get(`/documents/graph/${graphId}`);
    return response.data;
  },

  getDocumentStatus: async (documentId) => {
    const response = await apiClient.get(`/documents/${documentId}/status`);
    return response.data;
  },

  getDocumentUploadConfig: async () => {
    const response = await apiClient.get('/documents/config');
    return response.data;
  },

  processDocument: async (graphId, documentId, providerId) => {
    const body = { graphId };
    if (providerId) body.provider_id = providerId;
    const response = await apiClient.post(`/documents/${documentId}/process`, body, {
      timeout: 300000 // 5 min – processing can be slow
    });
    return response.data;
  },

  /**
   * Update processing parameters for a single document
   * @param {string} documentId - Document UUID
   * @param {object} params - Processing parameters
   * @param {number} [params.max_iterations_per_chunk] - Max iterations (1-5)
   * @param {number} [params.chunk_retries] - Chunk retries (0-5)
   * @param {string} [params.model] - LLM model (gpt-4o, gpt-4o-mini, gpt-5-mini)
   */
  updateDocumentParams: async (documentId, params) => {
    const response = await apiClient.patch(`/documents/${documentId}/params`, params);
    return response.data;
  },

  /**
   * Update processing parameters for all documents in a graph (bulk update)
   * @param {string} graphId - Graph UUID
   * @param {object} params - Processing parameters to apply to all documents
   */
  updateGraphDocumentsParams: async (graphId, params) => {
    const response = await apiClient.patch(`/documents/graph/${graphId}/params`, params);
    return response.data;
  },

  /**
   * Cancel processing for a document
   * @param {string} documentId - Document UUID
   * @returns {object} Result with success status
   */
  cancelDocument: async (documentId) => {
    const response = await apiClient.post(`/documents/${documentId}/cancel`);
    return response.data;
  },

  /**
   * Get processing output for a document
   * @param {string} documentId - Document UUID
   * @returns {object} Processing output JSON
   */
  getDocumentOutput: async (documentId) => {
    const response = await apiClient.get(`/documents/${documentId}/output`);
    return response.data;
  },

  /**
   * Process all unprocessed documents in a graph
   * @param {string} graphId - Graph UUID
   * @returns {object} Result with queued_count
   */
  processAllDocuments: async (graphId) => {
    const response = await apiClient.post(`/documents/graph/${graphId}/process-all`);
    return response.data;
  },

  // ============================================================================
  // Bulk Build API
  // ============================================================================

  bulkBuild: async (baseName, workspaceId, files, processingParams, description, onProgress) => {
    const formData = new FormData();
    formData.append('base_name', baseName);
    formData.append('workspace_id', workspaceId);
    if (description) formData.append('description', description);
    formData.append('processing_params', JSON.stringify(processingParams));
    files.forEach(file => formData.append('files', file));

    const response = await apiClient.post('/graphs/bulk-build', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 600000,
    });
    return response.data;
  },

  getBulkBuildStatus: async (documentIds) => {
    const response = await apiClient.get('/graphs/bulk-build/status', {
      params: { document_ids: documentIds.join(',') },
    });
    return response.data;
  },

  cancelBulkBuild: async (documentIds) => {
    const response = await apiClient.post('/graphs/bulk-build/cancel', {
      document_ids: documentIds,
    });
    return response.data;
  },

  /**
   * Process only pending (unprocessed) chunks for a graph
   * @param {string} graphId - Graph UUID
   * @returns {Promise<Object>} Processing result with pending_count
   */
  processPendingChunks: async (graphId) => {
    const response = await apiClient.post(`/chunks/graph/${graphId}/process-pending`);
    return response.data;
  },

  // ============================================================================
  // Graph Prompts API
  // ============================================================================

  /**
   * Get prompts for a graph (custom + defaults)
   * @param {string} graphId - Graph UUID
   * @returns {object} Prompts with customization info
   */
  getGraphPrompts: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/prompts`);
    return response.data;
  },

  /**
   * Update custom prompts for a graph
   * @param {string} graphId - Graph UUID
   * @param {object} prompts - Prompt key-value pairs to update
   * @returns {object} Result with customized_keys
   */
  updateGraphPrompts: async (graphId, prompts) => {
    const response = await apiClient.put(`/graphs/${graphId}/prompts`, { prompts });
    return response.data;
  },

  /**
   * Reset all prompts to defaults for a graph
   * @param {string} graphId - Graph UUID
   * @returns {object} Result
   */
  resetGraphPrompts: async (graphId) => {
    const response = await apiClient.delete(`/graphs/${graphId}/prompts`);
    return response.data;
  },

  /**
   * Get prompt customization status for a graph (for UI indicators)
   * @param {string} graphId - Graph UUID
   * @returns {object} Status with has_graph_customizations, has_document_overrides
   */
  getGraphPromptsStatus: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/prompts/status`);
    return response.data;
  },

  // ============================================================================
  // Document Prompts API
  // ============================================================================

  /**
   * Get prompts for a document (effective = document -> graph -> defaults)
   * @param {string} documentId - Document UUID
   * @returns {object} Prompts with source info
   */
  getDocumentPrompts: async (documentId) => {
    const response = await apiClient.get(`/documents/${documentId}/prompts`);
    return response.data;
  },

  /**
   * Update custom prompts for a document (document-level overrides)
   * @param {string} documentId - Document UUID
   * @param {object} prompts - Prompt key-value pairs to update
   * @returns {object} Result with customized_keys
   */
  updateDocumentPrompts: async (documentId, prompts) => {
    const response = await apiClient.put(`/documents/${documentId}/prompts`, { prompts });
    return response.data;
  },

  /**
   * Reset document prompts (remove document-level overrides)
   * @param {string} documentId - Document UUID
   * @returns {object} Result
   */
  resetDocumentPrompts: async (documentId) => {
    const response = await apiClient.delete(`/documents/${documentId}/prompts`);
    return response.data;
  },

  /**
   * Generate a custom graph extraction prompt for a document using AI.
   * Sends the document text + graph description to LLM with a meta-prompt template.
   * @param {string} documentId - Document UUID
   * @returns {object} Result with generated prompt
   */
  generateDocumentPrompt: async (documentId, buildApproach = null, providerId = null) => {
    const body = { build_approach: buildApproach };
    if (providerId) body.provider_id = providerId;
    const response = await apiClient.post(`/documents/${documentId}/generate-prompt`, body);
    return response.data;
  },

  /**
   * Download the original uploaded file for a document.
   * Fetches as blob and triggers a browser save dialog.
   */
  downloadDocumentFile: async (documentId, filename) => {
    const response = await apiClient.get(`/documents/${documentId}/file`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Delete a document (only unprocessed documents can be deleted)
   * @param {string} documentId - Document UUID
   * @returns {object} Result with success status
   */
  deleteDocument: async (documentId) => {
    const response = await apiClient.delete(`/documents/${documentId}`);
    return response.data;
  },

  // ============================================================================
  // Account-Level Default Prompts API
  // ============================================================================

  getAccountPrompts: async () => {
    const response = await apiClient.get('/graphs/account-prompts');
    return response.data;
  },

  updateAccountPrompts: async (prompts) => {
    const response = await apiClient.put('/graphs/account-prompts', { prompts });
    return response.data;
  },

  resetAccountPrompts: async () => {
    const response = await apiClient.delete('/graphs/account-prompts');
    return response.data;
  },

  // ============================================================================
  // Chunk Management API
  // ============================================================================

  /**
   * Chunk a document and save chunks to database
   * @param {string} graphId - Graph UUID
   * @param {string} documentId - Document UUID
   * @param {object} [params] - Optional chunking parameters
   * @returns {object} Result with chunks_created count and chunk_ids
   */
  chunkDocument: async (graphId, documentId, params = {}) => {
    const response = await apiClient.post(
      `/chunks/graph/${graphId}/document/${documentId}/chunk`,
      params,
      { timeout: 300000 } // 5 min – chunking large documents can be slow
    );
    return response.data;
  },

  /**
   * Get chunks for a graph (paginated)
   * @param {string} graphId - Graph UUID
   * @param {number} [page=1] - Page number
   * @param {number} [pageSize=10] - Items per page
   * @returns {object} Paginated chunks list with stats
   */
  getGraphChunks: async (graphId, page = 1, pageSize = 10) => {
    const response = await apiClient.get(`/chunks/graph/${graphId}`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  /**
   * Get chunk counts for a graph
   * @param {string} graphId - Graph UUID
   * @returns {object} Counts (total, pending, processed, failed)
   */
  getChunkCounts: async (graphId) => {
    const response = await apiClient.get(`/chunks/graph/${graphId}/count`);
    return response.data;
  },

  /**
   * Get a single chunk with Q/A
   * @param {string} chunkId - Chunk UUID
   * @returns {object} Chunk details with Q/A list
   */
  getChunk: async (chunkId) => {
    const response = await apiClient.get(`/chunks/${chunkId}`);
    return response.data;
  },

  /**
   * Add a manual chunk to a graph
   * @param {string} graphId - Graph UUID
   * @param {object} chunkData - Chunk data {text, title?, is_information_chunk?}
   * @returns {object} Created chunk info
   */
  addManualChunk: async (graphId, chunkData) => {
    const response = await apiClient.post(`/chunks/graph/${graphId}/manual`, chunkData);
    return response.data;
  },

  /**
   * Update a chunk
   * @param {string} chunkId - Chunk UUID
   * @param {object} updates - Fields to update {text?, title?, is_information_chunk?}
   * @returns {object} Success status
   */
  updateChunk: async (chunkId, updates) => {
    const response = await apiClient.patch(`/chunks/${chunkId}`, updates);
    return response.data;
  },

  /**
   * Delete a chunk
   * @param {string} chunkId - Chunk UUID
   * @returns {object} Success status
   */
  deleteChunk: async (chunkId) => {
    const response = await apiClient.delete(`/chunks/${chunkId}`);
    return response.data;
  },

  /**
   * Reorder chunks within a graph
   * @param {string} graphId - Graph UUID
   * @param {string[]} chunkOrder - Array of chunk_ids in desired order
   * @returns {object} Success status
   */
  reorderChunks: async (graphId, chunkOrder) => {
    const response = await apiClient.post(`/chunks/graph/${graphId}/reorder`, {
      chunk_order: chunkOrder
    });
    return response.data;
  },

  /**
   * Reset all chunks to pending status
   * @param {string} graphId - Graph UUID
   * @returns {object} Reset count
   */
  resetChunksStatus: async (graphId) => {
    const response = await apiClient.post(`/chunks/graph/${graphId}/reset`);
    return response.data;
  },

  // ============================================================================
  // Chunk Q/A API
  // ============================================================================

  /**
   * Get Q/A pairs for a chunk
   * @param {string} chunkId - Chunk UUID
   * @returns {object} Q/A list (user and system)
   */
  getChunkQA: async (chunkId) => {
    const response = await apiClient.get(`/chunks/${chunkId}/qa`);
    return response.data;
  },

  /**
   * Add a Q/A pair to a chunk
   * @param {string} chunkId - Chunk UUID
   * @param {string} question - Question text
   * @param {string} expectedAnswer - Expected answer text
   * @returns {object} Created Q/A info
   */
  addChunkQA: async (chunkId, question, expectedAnswer) => {
    const response = await apiClient.post(`/chunks/${chunkId}/qa`, {
      question,
      expected_answer: expectedAnswer
    });
    return response.data;
  },

  /**
   * Update a Q/A pair
   * @param {string} chunkQAId - Q/A UUID
   * @param {object} updates - Fields to update {question?, expected_answer?}
   * @returns {object} Success status
   */
  updateChunkQA: async (chunkQAId, updates) => {
    const response = await apiClient.patch(`/chunks/qa/${chunkQAId}`, updates);
    return response.data;
  },

  /**
   * Delete a Q/A pair
   * @param {string} chunkQAId - Q/A UUID
   * @returns {object} Success status
   */
  deleteChunkQA: async (chunkQAId) => {
    const response = await apiClient.delete(`/chunks/qa/${chunkQAId}`);
    return response.data;
  },

  /**
   * Search chunks by text or semantic similarity
   * @param {string} graphId - Graph UUID
   * @param {string} query - Search query
   * @param {string} [mode='text'] - Search mode: 'text' or 'semantic'
   * @param {number} [pageSize=20] - Max results
   * @returns {object} Result with chunks array
   */
  searchChunks: async (graphId, query, mode = 'text', pageSize = 20) => {
    const response = await apiClient.get(`/chunks/graph/${graphId}/search`, {
      params: { q: query, mode, page_size: pageSize }
    });
    return response.data;
  },

  /**
   * Fetch all nodes, synapses, and gates linked to specific chunk IDs
   * @param {string} graphId - Graph UUID
   * @param {string[]} chunkIds - Array of chunk UUIDs
   * @returns {object} Result with nodes, synapses, and gates arrays
   */
  getElementsByChunkIds: async (graphId, chunkIds) => {
    const response = await apiClient.get(`/graphs/${graphId}/elements-by-chunks`, {
      params: { chunkIds: chunkIds.join(',') }
    });
    return response.data;
  },

  /**
   * Fetch comprehensive graph statistics and quality score
   * @param {string} graphId - Graph UUID
   * @returns {object} Graph stats including counts, distributions, connectivity, quality score
   */
  getGraphStats: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/stats`, { timeout: 120000 });
    return response.data;
  },

  /**
   * Force a recompute of the persisted GraphStats row and return the
   * fresh stats blob + timestamp metadata.
   * Mirrors getGraphStats but POSTs to /stats/refresh.
   * @param {string} graphId - Graph UUID
   * @returns {object} Same shape as getGraphStats, with stats_meta.stats_refreshed_at updated.
   */
  refreshGraphStats: async (graphId) => {
    const response = await apiClient.post(`/graphs/${graphId}/stats/refresh`, null, { timeout: 180000 });
    return response.data;
  },

  /**
   * Build and cache vocabulary context for a graph
   * @param {string} graphId - Graph UUID
   * @returns {object} Built vocabulary context
   */
  buildGraphVocabulary: async (graphId) => {
    const response = await apiClient.post(`/graphs/${graphId}/vocabulary/build`);
    return response.data;
  },

  /**
   * Get cached vocabulary context for a graph
   * @param {string} graphId - Graph UUID
   * @returns {object} { exists: boolean, vocabulary: object|null }
   */
  getGraphVocabulary: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/vocabulary`);
    return response.data;
  },

  /**
   * Preview query expansion using graph vocabulary context (no Platform execution)
   * @param {string} graphId - Graph UUID
   * @param {string} query - User query to expand
   * @returns {object} { original_query, expanded_query, expansion_notes }
   */
  expandQueryPreview: async (graphId, query) => {
    const response = await apiClient.post(`/graphs/${graphId}/expand-query`, { query });
    return response.data;
  },

  // ============================================================================
  // Node Management API (Delete, Restore, Search)
  // ============================================================================

  /**
   * Soft delete a node
   * @param {string} nodeId - Node UUID
   * @returns {object} Result with deletedAt timestamp and canRestore flag
   */
  deleteNode: async (nodeId) => {
    const response = await apiClient.delete(`/nodes/${nodeId}`);
    return response.data;
  },

  /**
   * Restore a soft-deleted node
   * @param {string} nodeId - Node UUID
   * @returns {object} Result with restored node
   */
  restoreNode: async (nodeId) => {
    const response = await apiClient.post(`/nodes/${nodeId}/restore`);
    return response.data;
  },

  /**
   * Re-sync a node to all Pinecone namespaces.
   * @param {string} nodeId - Node UUID
   * @returns {object} Result with namespaces updated
   */
  reindexNode: async (nodeId) => {
    const response = await apiClient.post(`/nodes/${nodeId}/reindex`);
    return response.data;
  },

  /**
   * Bulk delete multiple nodes
   * @param {string[]} nodeIds - Array of node UUIDs
   * @returns {object} Result with deletedCount and canRestore flag
   */
  bulkDeleteNodes: async (nodeIds) => {
    const response = await apiClient.post('/nodes/bulk-delete', { nodeIds });
    return response.data;
  },

  /**
   * Advanced search for nodes (text or semantic)
   * @param {object} params - Search parameters
   * @param {string} params.graphId - Graph UUID (required)
   * @param {string} [params.q] - Search query
   * @param {string} [params.mode] - 'text' or 'semantic'
   * @param {string} [params.nodeType] - Filter by node type
   * @param {boolean} [params.includeDeleted] - Include soft-deleted nodes
   * @param {boolean} [params.orphanOnly] - Only return orphan nodes
   * @param {number} [params.page] - Page number
   * @param {number} [params.pageSize] - Results per page
   * @returns {object} Result with nodes array and pagination info
   */
  searchNodes: async (params) => {
    const response = await apiClient.get('/nodes/search', { params });
    return response.data;
  },

  /**
   * List soft-deleted nodes for a graph (for restore UI)
   * @param {string} graphId - Graph UUID
   * @param {number} [page] - Page number
   * @param {number} [pageSize] - Results per page
   * @returns {object} Result with deleted nodes
   */
  listDeletedNodes: async (graphId, page = 1, pageSize = 25) => {
    const response = await apiClient.get('/nodes/deleted', { 
      params: { graphId, page, pageSize } 
    });
    return response.data;
  },

  // ============================================================================
  // Synapse Management API (Delete, Restore, Search)
  // ============================================================================

  /**
   * Soft delete a synapse
   * @param {string} synapseId - Synapse UUID
   * @returns {object} Result with deletedAt timestamp and canRestore flag
   */
  deleteSynapse: async (synapseId) => {
    const response = await apiClient.delete(`/edges/${synapseId}`);
    return response.data;
  },

  /**
   * Restore a soft-deleted synapse
   * @param {string} synapseId - Synapse UUID
   * @returns {object} Result with restored synapse
   */
  restoreSynapse: async (synapseId) => {
    const response = await apiClient.post(`/edges/${synapseId}/restore`);
    return response.data;
  },

  /**
   * Bulk delete multiple synapses
   * @param {string[]} synapseIds - Array of synapse UUIDs
   * @returns {object} Result with deletedCount and canRestore flag
   */
  bulkDeleteSynapses: async (synapseIds) => {
    const response = await apiClient.post('/edges/bulk-delete', { synapseIds });
    return response.data;
  },

  /**
   * Advanced search for synapses
   * @param {object} params - Search parameters
   * @param {string} params.graphId - Graph UUID (required)
   * @param {string} [params.q] - Search query
   * @param {string} [params.nodeId] - Filter by connected node
   * @param {boolean} [params.includeDeleted] - Include soft-deleted synapses
   * @param {number} [params.page] - Page number
   * @param {number} [params.pageSize] - Results per page
   * @returns {object} Result with synapses array and pagination info
   */
  searchSynapses: async (params) => {
    const response = await apiClient.get('/edges/search', { params });
    return response.data;
  },

  /**
   * List soft-deleted synapses for a graph (for restore UI)
   * @param {string} graphId - Graph UUID
   * @param {number} [page] - Page number
   * @param {number} [pageSize] - Results per page
   * @returns {object} Result with deleted synapses
   */
  listDeletedSynapses: async (graphId, page = 1, pageSize = 25) => {
    const response = await apiClient.get('/edges/deleted', { 
      params: { graphId, page, pageSize } 
    });
    return response.data;
  },

  // ============================================================================
  // Gate Management API (CRUD for logical AND/OR/NOT gates)
  // ============================================================================

  /**
   * List all gates for a graph
   * @param {object} params - Query parameters
   * @param {string} params.graphId - Graph UUID (required)
   * @param {string} [params.gateType] - Filter by gate type (AND, OR, NOT, XOR)
   * @param {boolean} [params.includeDeleted] - Include soft-deleted gates
   * @param {number} [params.page] - Page number
   * @param {number} [params.pageSize] - Results per page
   * @returns {object} Result with gates array and pagination info
   */
  listGates: async (params) => {
    const response = await apiClient.get('/gates', { params });
    return response.data;
  },

  /**
   * Get a single gate with its connections
   * @param {string} gateId - Gate UUID
   * @returns {object} Result with gate details and input/output synapses
   */
  getGate: async (gateId) => {
    const response = await apiClient.get(`/gates/${gateId}`);
    return response.data;
  },

  /**
   * Create a new gate
   * @param {object} data - Gate data
   * @param {string} data.graphId - Graph UUID (required)
   * @param {string} data.gateType - Gate type: AND, OR, NOT, XOR (required)
   * @param {string} [data.name] - Gate name
   * @param {string} [data.description] - Gate description
   * @param {string} [data.condition] - Gate condition expression
   * @param {string[]} [data.inputs] - Array of input node IDs
   * @param {string[]} [data.outputs] - Array of output node IDs
   * @returns {object} Result with created gate
   */
  createGate: async (data) => {
    const response = await apiClient.post('/gates', data);
    return response.data;
  },

  /**
   * Update a gate
   * @param {string} gateId - Gate UUID
   * @param {object} data - Gate data to update
   * @param {string} [data.name] - Gate name
   * @param {string} [data.description] - Gate description
   * @param {string} [data.gateType] - Gate type
   * @param {string} [data.condition] - Gate condition
   * @returns {object} Result with updated gate
   */
  updateGate: async (gateId, data) => {
    const response = await apiClient.put(`/gates/${gateId}`, data);
    return response.data;
  },

  /**
   * Soft delete a gate
   * @param {string} gateId - Gate UUID
   * @returns {object} Result with deletedAt timestamp and canRestore flag
   */
  deleteGate: async (gateId) => {
    const response = await apiClient.delete(`/gates/${gateId}`);
    return response.data;
  },

  /**
   * Restore a soft-deleted gate
   * @param {string} gateId - Gate UUID
   * @returns {object} Result with restored gate
   */
  restoreGate: async (gateId) => {
    const response = await apiClient.post(`/gates/${gateId}/restore`);
    return response.data;
  },

  /**
   * Batch create multiple gates in a single request
   * @param {object} data - Batch data
   * @param {string} data.graphId - Graph UUID (required)
   * @param {Array} data.rules - Array of rule objects, each with:
   *   @param {string} rule.gateType - Gate type: AND, OR, NOT, XOR (required)
   *   @param {string} [rule.name] - Gate name (auto-generated if omitted)
   *   @param {string} [rule.description] - Gate description
   *   @param {string[]} rule.inputs - Array of input node IDs (required)
   *   @param {string[]} rule.outputs - Array of output node IDs (required)
   * @returns {object} Result with results array and summary
   */
  batchCreateGates: async (data) => {
    const response = await apiClient.post('/gates/batch', data);
    return response.data;
  },

  // ============================================================================
  // Execution Insights API
  // ============================================================================

  /**
   * Save an execution insight after a pipeline run
   * @param {object} data - Insight data (graph_id, query, execution_trace, etc.)
   * @returns {object} Result with insight_id
   */
  saveExecutionInsight: async (data) => {
    const response = await apiClient.post('/execution-insights', data);
    return response.data;
  },

  /**
   * List execution insights for a graph (paginated, lightweight)
   * @param {string} graphId - Graph UUID
   * @param {number} [page] - Page number
   * @param {number} [pageSize] - Results per page
   * @returns {object} Result with insights list and total_count
   */
  listExecutionInsights: async (graphId, page = 1, pageSize = 25) => {
    const response = await apiClient.get('/execution-insights', {
      params: { graph_id: graphId, page, page_size: pageSize }
    });
    return response.data;
  },

  /**
   * Get a single execution insight with full trace data
   * @param {string} insightId - Insight UUID
   * @returns {object} Result with full insight including execution_trace
   */
  getExecutionInsight: async (insightId) => {
    const response = await apiClient.get(`/execution-insights/${insightId}`);
    return response.data;
  },

  /**
   * Get on-demand five-pillar explainability report for a stored insight
   * @param {string} insightId - Insight UUID
   * @returns {object} Result with five-pillar report
   */
  getExplainabilityReport: async (insightId) => {
    const response = await apiClient.get(`/execution-insights/${insightId}/explain`);
    return response.data;
  },

  /**
   * Get LLM-narrated plain-English summaries for each pillar
   * @param {string} insightId - Insight UUID
   * @returns {object} Result with narrations per pillar (cached after first call)
   */
  getNarration: async (insightId) => {
    const response = await apiClient.get(`/execution-insights/${insightId}/explain/narrate`);
    return response.data;
  },

  /**
   * Get a formal constraint-satisfaction certificate for audit/compliance
   * @param {string} insightId - Insight UUID
   * @returns {object} Result with certificate JSON
   */
  getConstraintCertificate: async (insightId) => {
    const response = await apiClient.get(`/execution-insights/${insightId}/certificate`);
    return response.data;
  },

  getCompliancePackage: async (insightId, domain = 'general') => {
    const response = await apiClient.get(`/execution-insights/${insightId}/compliance-package`, {
      params: { domain },
      responseType: 'arraybuffer',
    });
    return response.data;
  },

  // =========================================================================
  // Execution Insight Feedback
  // =========================================================================

  /**
   * Submit chat feedback for an execution insight.
   * @param {string} insightId
   * @param {{ rating: number, comment?: string }} payload
   */
  saveInsightFeedback: async (insightId, payload) => {
    const response = await apiClient.post(`/execution-insights/${insightId}/feedback`, payload);
    return response.data;
  },

  /** List all feedback rows for one insight. */
  listInsightFeedback: async (insightId) => {
    const response = await apiClient.get(`/execution-insights/${insightId}/feedback`);
    return response.data;
  },

  /** Graph-scoped feedback list joined with insight summary fields. */
  listGraphFeedback: async (graphId, { rating, status, page = 1, pageSize = 50 } = {}) => {
    const response = await apiClient.get('/execution-insights/feedback', {
      params: {
        graph_id: graphId,
        rating: rating ?? undefined,
        status: status ?? undefined,
        page,
        page_size: pageSize,
      },
    });
    return response.data;
  },

  /** Patch a feedback row (rating / comment / status). */
  updateInsightFeedback: async (feedbackId, payload) => {
    const response = await apiClient.patch(`/execution-insights/feedback/${feedbackId}`, payload);
    return response.data;
  },

  /** Trigger or refresh AI suggestions for a feedback row. */
  generateFeedbackSuggestions: async (feedbackId, payload = {}) => {
    const response = await apiClient.post(`/execution-insights/feedback/${feedbackId}/suggest`, payload);
    return response.data;
  },

  // =========================================================================
  // Graph Test Cases (per-graph regression suite)
  // =========================================================================

  /** List test cases for a graph (includes latest run per case). */
  listGraphTestCases: async (graphId) => {
    const response = await apiClient.get('/graph-test-cases', {
      params: { graph_id: graphId },
    });
    return response.data;
  },

  /** Create a single test case. */
  createGraphTestCase: async (payload) => {
    const response = await apiClient.post('/graph-test-cases', payload);
    return response.data;
  },

  /** Patch (or replace) a test case. */
  updateGraphTestCase: async (testCaseId, payload) => {
    const response = await apiClient.patch(`/graph-test-cases/${testCaseId}`, payload);
    return response.data;
  },

  /** Delete a test case (cascades to its run history). */
  deleteGraphTestCase: async (testCaseId) => {
    const response = await apiClient.delete(`/graph-test-cases/${testCaseId}`);
    return response.data;
  },

  /** Bulk create N cases for a graph. */
  bulkCreateGraphTestCases: async (graphId, cases, workspaceId = null) => {
    const response = await apiClient.post('/graph-test-cases/bulk', {
      graph_id: graphId,
      workspace_id: workspaceId,
      cases,
    });
    return response.data;
  },

  /**
   * Parse markdown with `## Q&A Set` blocks. With `commit: false` (default)
   * returns the parsed cases for preview; with `commit: true` persists them.
   */
  importGraphTestCasesFromMarkdown: async (graphId, markdown, { commit = false, workspaceId = null } = {}) => {
    const response = await apiClient.post('/graph-test-cases/import-markdown', {
      graph_id: graphId,
      markdown,
      commit,
      workspace_id: workspaceId,
    });
    return response.data;
  },

  /** Persist a single test run result. */
  recordGraphTestRun: async (payload) => {
    const response = await apiClient.post('/graph-test-runs', payload);
    return response.data;
  },

  /** List recent runs for a single test case. */
  listGraphTestRuns: async (testCaseId, limit = 25) => {
    const response = await apiClient.get('/graph-test-runs', {
      params: { test_case_id: testCaseId, limit },
    });
    return response.data;
  },

  // =========================================================================
  // Quick Add — simplified node/synapse creation with auto-populated Platform fields
  // =========================================================================

  /**
   * Quick-add a node with minimal input. Platform fields are auto-populated.
   * @param {Object} data
   * @param {string} data.graphId - Graph UUID (required)
   * @param {string} data.name - Node name (required)
   * @param {string} data.description - Node description (required)
   * @param {string} data.kind - Node kind e.g. "Concept" (required)
   * @param {string} [data.sourceText] - Optional verbatim source text
   * @returns {Promise<Object>}
   */
  quickAddNode: async (data) => {
    const response = await apiClient.post('/nodes/quick-add', data);
    return response.data;
  },

  /**
   * Quick-add a synapse with minimal input. Platform fields are auto-populated.
   * @param {Object} data
   * @param {string} data.graphId - Graph UUID (required)
   * @param {string} data.sourceNodeId - Source node UUID (required)
   * @param {string} data.targetNodeId - Target node UUID (required)
   * @param {string} data.relation - Relation label (required)
   * @param {string} [data.description] - Optional description
   * @returns {Promise<Object>}
   */
  quickAddSynapse: async (data) => {
    const response = await apiClient.post('/edges/quick-add', data);
    return response.data;
  },

  /**
   * Recompile graph Platform artifacts without reprocessing documents/chunks.
   * @param {string} graphId - Graph UUID
   * @returns {Promise<Object>}
   */
  recompileGraph: async (graphId) => {
    const response = await apiClient.post(`/graph-management/${graphId}/recompile`);
    return response.data;
  },

  // ========================================================================
  // Demo Requests
  // ========================================================================

  /**
   * Submit a demo request (public, no auth)
   * @param {Object} data - Form data
   * @returns {Promise<Object>}
   */
  submitDemoRequest: async (data) => {
    const response = await axios.post('/api/demo-requests', data);
    return response.data;
  },

  /**
   * List demo requests (admin)
   * @param {Object} params - Query params (search, status, industry, etc.)
   * @returns {Promise<Object>}
   */
  listDemoRequests: async (params = {}) => {
    const response = await apiClient.get('/demo-requests', { params });
    return response.data;
  },

  /**
   * Get a single demo request by ID (admin)
   * @param {string} id - Demo request UUID
   * @returns {Promise<Object>}
   */
  getDemoRequest: async (id) => {
    const response = await apiClient.get(`/demo-requests/${id}`);
    return response.data;
  },

  /**
   * Update a demo request (admin)
   * @param {string} id - Demo request UUID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>}
   */
  updateDemoRequest: async (id, data) => {
    const response = await apiClient.put(`/demo-requests/${id}`, data);
    return response.data;
  },

  /**
   * Export demo requests as CSV (admin)
   * @param {Object} params - Filter params
   * @returns {Promise<string>} CSV content
   */
  exportDemoRequests: async (params = {}) => {
    const response = await apiClient.get('/demo-requests/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // =========================================================================
  // Router Graph Management
  // =========================================================================

  getRouterTargets: async (graphId) => {
    const response = await apiClient.get(`/graphs/${graphId}/router/targets`);
    return response.data;
  },

  addRouterTargets: async (graphId, targetIds, { targetType = 'graph', queryInputKey = 'nl_query' } = {}) => {
    const body = { target_type: targetType };
    if (targetType === 'process') {
      body.target_process_ids = targetIds;
      body.query_input_key = queryInputKey;
    } else {
      body.target_graph_ids = targetIds;
    }
    const response = await apiClient.post(`/graphs/${graphId}/router/targets`, body);
    return response.data;
  },

  removeRouterTarget: async (graphId, targetId) => {
    const response = await apiClient.delete(`/graphs/${graphId}/router/targets/${targetId}`);
    return response.data;
  },

  updateRouterTargetMarkers: async (graphId, targetId, queryMarkers, customDescription = null) => {
    const response = await apiClient.put(`/graphs/${graphId}/router/targets/${targetId}/markers`, {
      query_markers: queryMarkers,
      custom_description: customDescription,
    });
    return response.data;
  },

  generateRouterChunks: async (graphId, processingParams) => {
    const body = processingParams ? { processing_params: processingParams } : {};
    const response = await apiClient.post(`/graphs/${graphId}/router/generate-chunks`, body);
    return response.data;
  },

  ...synapseService,
  ...nodeService,
  ...nodeKindService,
  ...synapseKindService,

  // ── LLM Providers ────────────────────────────────────────────────
  listLLMProviders: async () => {
    const response = await apiClient.get('/llm-providers');
    return response.data;
  },
  createLLMProvider: async (data) => {
    const response = await apiClient.post('/llm-providers', data);
    return response.data;
  },
  updateLLMProvider: async (id, data) => {
    const response = await apiClient.put(`/llm-providers/${id}`, data);
    return response.data;
  },
  deleteLLMProvider: async (id) => {
    const response = await apiClient.delete(`/llm-providers/${id}`);
    return response.data;
  },
  testLLMProvider: async (id) => {
    const response = await apiClient.post(`/llm-providers/${id}/test`);
    return response.data;
  },
  setDefaultLLMProvider: async (id) => {
    const response = await apiClient.put(`/llm-providers/${id}/default`);
    return response.data;
  },
  getLLMProviderFull: async (id) => {
    const response = await apiClient.get(`/llm-providers/${id}/full`);
    return response.data;
  },

  // ── Coding LLM Providers ───────────────────────────────────────────
  listCodingLLMProviders: async () => {
    const response = await apiClient.get('/coding-llm-providers');
    return response.data;
  },
  createCodingLLMProvider: async (data) => {
    const response = await apiClient.post('/coding-llm-providers', data);
    return response.data;
  },
  updateCodingLLMProvider: async (id, data) => {
    const response = await apiClient.put(`/coding-llm-providers/${id}`, data);
    return response.data;
  },
  deleteCodingLLMProvider: async (id) => {
    const response = await apiClient.delete(`/coding-llm-providers/${id}`);
    return response.data;
  },
  testCodingLLMProvider: async (id) => {
    const response = await apiClient.post(`/coding-llm-providers/${id}/test`);
    return response.data;
  },
  setDefaultCodingLLMProvider: async (id) => {
    const response = await apiClient.put(`/coding-llm-providers/${id}/default`);
    return response.data;
  },

  // ── Workspace Secrets (write-only metadata; values never returned) ──
  listWorkspaceSecrets: async (workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.get('/workspace-secrets', { headers });
    return response.data;
  },
  createWorkspaceSecret: async (data, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.post('/workspace-secrets', data, { headers });
    return response.data;
  },
  updateWorkspaceSecret: async (id, data, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.put(`/workspace-secrets/${id}`, data, { headers });
    return response.data;
  },
  deleteWorkspaceSecret: async (id, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.delete(`/workspace-secrets/${id}`, { headers });
    return response.data;
  },

  // ── Workspace MCP Servers (registry for the Python node cm_mcp helper) ──
  listMcpServers: async (workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.get('/mcp-servers', { headers });
    return response.data;
  },
  getMcpServer: async (id, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.get(`/mcp-servers/${id}`, { headers });
    return response.data;
  },
  createMcpServer: async (data, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.post('/mcp-servers', data, { headers });
    return response.data;
  },
  updateMcpServer: async (id, data, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.put(`/mcp-servers/${id}`, data, { headers });
    return response.data;
  },
  deleteMcpServer: async (id, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.delete(`/mcp-servers/${id}`, { headers });
    return response.data;
  },
  testMcpServer: async (id, workspaceId) => {
    const headers = workspaceId ? { 'X-Workspace-Id': workspaceId } : {};
    const response = await apiClient.post(`/mcp-servers/${id}/test`, {}, { headers });
    return response.data;
  },

  // ── Transfer Environments (Flask backend CRUD) ──────────────────
  listTransferEnvironments: async () => {
    const response = await apiClient.get('/transfer-environments');
    return response.data;
  },
  createTransferEnvironment: async (data) => {
    const response = await apiClient.post('/transfer-environments', data);
    return response.data;
  },
  updateTransferEnvironment: async (id, data) => {
    const response = await apiClient.put(`/transfer-environments/${id}`, data);
    return response.data;
  },
  deleteTransferEnvironment: async (id) => {
    const response = await apiClient.delete(`/transfer-environments/${id}`);
    return response.data;
  },
  testTransferEnvironment: async (id) => {
    const response = await apiClient.post(`/transfer-environments/${id}/test`);
    return response.data;
  },

  // ── Cache Management (Redis via Flask) ────────────────────────────
  getCacheSummary: async () => {
    const response = await apiClient.get('/cache-management/summary');
    return response.data;
  },
  deleteRedisSessions: async (keys = [], all = false) => {
    const response = await apiClient.delete('/cache-management/redis/sessions', {
      data: all ? { all: true } : { keys },
    });
    return response.data;
  },
  deleteRedisPrompts: async (keys = [], all = false) => {
    const response = await apiClient.delete('/cache-management/redis/prompts', {
      data: all ? { all: true } : { keys },
    });
    return response.data;
  },
  deleteRedisIdempotency: async (keys = [], all = false) => {
    const response = await apiClient.delete('/cache-management/redis/idempotency', {
      data: all ? { all: true } : { keys },
    });
    return response.data;
  },
  deleteAllRedis: async () => {
    const response = await apiClient.delete('/cache-management/redis/all', {
      data: { confirm: true },
    });
    return response.data;
  },

  // ── Graph Pool Stats (proxied from GraphBuilder) ─────────────────
  getGraphPoolStats: async () => {
    const response = await apiClient.get('/cache-management/graph-pools');
    return response.data;
  },
  evictGraphPool: async (graphId) => {
    const response = await apiClient.delete(`/cache-management/graph-pools/${graphId}`);
    return response.data;
  },
  reloadGraphPool: async (graphId) => {
    const response = await apiClient.post(`/cache-management/graph-pools/${graphId}/reload`);
    return response.data;
  },

  // ── Lookup Reference Data ────────────────────────────────────────
  listLookup: async ({ category, search, page = 1, pageSize = 100 } = {}) => {
    const params = { page, pageSize };
    if (category) params.category = category;
    if (search) params.q = search;
    const response = await apiClient.get('/lookup', { params });
    return response.data;
  },

  getLookupCategories: async () => {
    const response = await apiClient.get('/lookup/categories');
    return response.data;
  },

  getLookupByCode: async (code, category) => {
    const params = { code };
    if (category) params.category = category;
    const response = await apiClient.get('/lookup/by-code', { params });
    return response.data;
  },

  getLookup: async (lookupId) => {
    const response = await apiClient.get(`/lookup/${lookupId}`);
    return response.data;
  },

  createLookup: async (payload) => {
    const response = await apiClient.post('/lookup', payload);
    return response.data;
  },

  updateLookup: async (lookupId, payload) => {
    const response = await apiClient.put(`/lookup/${lookupId}`, payload);
    return response.data;
  },

  deleteLookup: async (lookupId) => {
    const response = await apiClient.delete(`/lookup/${lookupId}`);
    return response.data;
  },

  previewLookupUpload: async (file, options) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options || {}));
    const response = await apiClient.post('/lookup/upload/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  commitLookupUpload: async (file, options) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options || {}));
    const response = await apiClient.post('/lookup/upload/commit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  ingestLookupCmsPack: async (files, options) => {
    const formData = new FormData();
    (files || []).forEach((f) => formData.append('files', f));
    formData.append('options', JSON.stringify(options || {}));
    const response = await apiClient.post('/lookup/upload/cms-pack', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 900000,
    });
    return response.data;
  },

  /**
   * Apply domain ontology classification to an existing graph's nodes.
   * Calls the GraphBuilder retroactive endpoint.
   */
  applyDomainOntology: async (graphId, options = {}) => {
    const response = await graphBuilderClient.post(
      `/api/graphs/${graphId}/domain-ontology`,
      {
        model: options.model || 'gpt-4o-mini',
        batch_size: options.batchSize || 20,
        concurrency: options.concurrency || 8,
        budget: options.budget || -1,
      },
      { timeout: 600000 },
    );
    return response.data;
  },
};

export default apiService;
export { apiClient, graphBuilderClient };

